import {
    Box,
    Card,
    CardContent,
    Divider,
    Link,
    Stack,
    Typography,
    CircularProgress,
} from '@mui/material';
import {
    EditButton,
    RecordContextProvider,
    ReferenceField,
    ShowBase,
    Title,
    useShowContext,
} from 'react-admin';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import { useResource } from '@app/misc/useResource';
import { Organization, ResourceLight } from '@wwf/sdk';

const LinkOrDash = ({
    href,
    label,
}: {
    href: string | null | undefined;
    label: string;
}) =>
    href ? (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
        >
            {label.trim() || 'Open link'}
        </Link>
    ) : (
        <Typography variant="body2" color="text.secondary">
            —
        </Typography>
    );

const boolLabel = (v: boolean | undefined) =>
    v === undefined ? '—' : v ? 'Yes' : 'No';

const OrganizationLogoPreview = ({
    logo,
    name,
}: {
    logo: ResourceLight | null | undefined;
    name: string | null | undefined;
}) => {
    const { data: logoUrl } = useResource(logo?.id);

    return logoUrl ? (
        <Box
            component="img"
            src={logoUrl}
            alt={name?.trim() ? `${name.trim()} logo` : 'Organization logo'}
            sx={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain' }}
        />
    ) : (
        <Typography variant="body2" color="text.secondary">
            —
        </Typography>
    );
};

const MascotHugPreview = ({
    image,
    name,
}: {
    image: ResourceLight | null | undefined;
    name: string | null | undefined;
}) => {
    const { data: imageUrl } = useResource(image?.id);

    return imageUrl ? (
        <Box
            component="img"
            src={imageUrl}
            alt={
                name?.trim()
                    ? `${name.trim()} mascot hug image`
                    : 'Mascot hug image'
            }
            sx={{ maxHeight: 100, maxWidth: 100, objectFit: 'contain' }}
        />
    ) : (
        <Typography variant="body2" color="text.secondary">
            —
        </Typography>
    );
};

const OrganizationChatAvatarPreview = ({
    avatar,
    assistantName,
}: {
    avatar: ResourceLight | null | undefined;
    assistantName: string | null | undefined;
}) => {
    const { data: imageUrl } = useResource(avatar?.id);

    return imageUrl ? (
        <Box
            component="img"
            src={imageUrl}
            alt={
                assistantName?.trim()
                    ? `${assistantName.trim()} chatbot assistant avatar`
                    : 'Chatbot assistant avatar'
            }
            sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                objectFit: 'cover',
            }}
        />
    ) : (
        <Typography variant="body2" color="text.secondary">
            —
        </Typography>
    );
};

const multilineOrDash = (text: string | null | undefined) => {
    const t = text?.trim();
    return t ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {text}
        </Typography>
    ) : (
        <Typography variant="body2" color="text.secondary">
            —
        </Typography>
    );
};

const OrganizationShowContent = () => {
    const { record, isPending, error } = useShowContext<Organization>();

    if (isPending) {
        return (
            <Stack alignItems="center" py={6} width="100%">
                <CircularProgress size={32} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack sx={{ width: '100%' }} gap={2}>
                <Title title="Organization settings" />
                <Typography color="error" variant="body2">
                    Could not load organization settings.
                </Typography>
            </Stack>
        );
    }

    if (!record) {
        return (
            <Stack sx={{ width: '100%' }} gap={2}>
                <Title title="Organization settings" />
                <Typography variant="body2" color="text.secondary">
                    No organization record found.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack sx={{ width: '100%' }} gap={2}>
            <Title title="Organization settings" />
            <Card>
                <CardContent>
                    <Stack gap={3}>
                        <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            gap={2}
                            flexWrap="wrap"
                        >
                            <Box>
                                <Typography
                                    variant="h5"
                                    component="h1"
                                    gutterBottom
                                >
                                    {record.name?.trim() || 'Organization'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    ID: {record.id}
                                </Typography>
                            </Box>
                            <EditButton
                                record={record}
                                variant="contained"
                                label="Edit"
                            />
                        </Stack>

                        <Divider />

                        <Stack gap={1}>
                            <Typography variant="h6">Branding</Typography>
                            <LabelValueRow
                                label="Logo"
                                value={
                                    record.logo ? (
                                        <OrganizationLogoPreview
                                            logo={record.logo}
                                            name={record.name}
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            —
                                        </Typography>
                                    )
                                }
                            />
                        </Stack>

                        <Divider />

                        <Stack gap={1}>
                            <Typography variant="h6">Chatbot</Typography>
                            <LabelValueRow
                                label="Assistant name"
                                alignItems="flex-start"
                                value={
                                    record.chatbotAssistantName?.trim() ? (
                                        record.chatbotAssistantName
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            —
                                        </Typography>
                                    )
                                }
                            />
                            <LabelValueRow
                                label="Assistant avatar"
                                alignItems="flex-start"
                                value={
                                    record.chatbotAssistantAvatar ? (
                                        <OrganizationChatAvatarPreview
                                            avatar={
                                                record.chatbotAssistantAvatar
                                            }
                                            assistantName={
                                                record.chatbotAssistantName
                                            }
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            —
                                        </Typography>
                                    )
                                }
                            />
                            <LabelValueRow
                                label="Assistant description"
                                alignItems="flex-start"
                                value={multilineOrDash(
                                    record.chatbotAssistantDescription
                                )}
                            />
                            <LabelValueRow
                                label="Organization description"
                                alignItems="flex-start"
                                value={multilineOrDash(
                                    record.chatbotOrganizationDescription
                                )}
                            />
                        </Stack>

                        <Divider />

                        <Stack gap={1}>
                            <Typography variant="h6">Links</Typography>
                            <LabelValueRow
                                label="Drive resources link"
                                value={
                                    <LinkOrDash
                                        href={
                                            record.driveResourcesLink ??
                                            undefined
                                        }
                                        label={record.driveResourcesLink ?? ''}
                                    />
                                }
                            />
                            <LabelValueRow
                                label="Main volunteers community WhatsApp"
                                value={
                                    <LinkOrDash
                                        href={
                                            record.mainVolunteersCommunityWhatsappLink ??
                                            undefined
                                        }
                                        label={
                                            record.mainVolunteersCommunityWhatsappLink ??
                                            ''
                                        }
                                    />
                                }
                            />
                        </Stack>

                        <Divider />

                        <Stack gap={1}>
                            <Typography variant="h6">References</Typography>
                            <RecordContextProvider value={record}>
                                <LabelValueRow
                                    label="Antenna headquarter"
                                    value={
                                        record.antennaHeadquarterId ? (
                                            <ReferenceField
                                                source="antennaHeadquarterId"
                                                reference="antenna"
                                                link={false}
                                            >
                                                <AntennaLightCard />
                                            </ReferenceField>
                                        ) : (
                                            '—'
                                        )
                                    }
                                />
                                <LabelValueRow
                                    label="Mascot panda animation"
                                    value={
                                        record.mascotAnimationId ? (
                                            <ReferenceField
                                                source="mascotAnimationId"
                                                reference="animation"
                                                link={false}
                                            >
                                                <AnimationLightCard />
                                            </ReferenceField>
                                        ) : (
                                            '—'
                                        )
                                    }
                                />
                                <LabelValueRow
                                    label="Mascot hug image"
                                    value={
                                        record.mascotHugImage ? (
                                            <MascotHugPreview
                                                image={record.mascotHugImage}
                                                name={record.name}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                —
                                            </Typography>
                                        )
                                    }
                                />
                            </RecordContextProvider>
                        </Stack>

                        <Divider />

                        <Stack gap={2}>
                            <Typography variant="h6">Features</Typography>
                            <Stack gap={0.5}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Main volunteers
                                </Typography>
                                <LabelValueRow
                                    label="Welcome phrase"
                                    value={boolLabel(
                                        record.enableWelcomePhraseForMainVolunteers
                                    )}
                                />
                                <LabelValueRow
                                    label="Chatbot"
                                    value={boolLabel(
                                        record.enableChatbotForMainVolunteers
                                    )}
                                />
                            </Stack>
                            <Stack gap={0.5}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Volunteers
                                </Typography>
                                <LabelValueRow
                                    label="Welcome phrase"
                                    value={boolLabel(
                                        record.enableWelcomePhraseForVolunteers
                                    )}
                                />
                                <LabelValueRow
                                    label="Chatbot"
                                    value={boolLabel(
                                        record.enableChatbotForVolunteers
                                    )}
                                />
                            </Stack>
                            <Stack gap={0.5}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Staff
                                </Typography>
                                <LabelValueRow
                                    label="Welcome phrase"
                                    value={boolLabel(
                                        record.enableWelcomePhraseForStaff
                                    )}
                                />
                                <LabelValueRow
                                    label="Chatbot"
                                    value={boolLabel(
                                        record.enableChatbotForStaff
                                    )}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
};

export const OrganizationShow = () => (
    <ShowBase resource="organization" redirectOnError={false}>
        <OrganizationShowContent />
    </ShowBase>
);
